import type { ImConversationDto, ImMessageDto } from './message-api'
import type {
  ChatMessage,
  ChatMessageFrom,
  ChatMessageKind,
  Conversation,
  ConversationKind,
  ConversationMember,
  MessageRecord,
  MessageType,
} from '../types'

const TYPE_SET = new Set<MessageType>(['system', 'business', 'alert'])
const KIND_SET = new Set<ConversationKind>(['direct', 'group', 'bot', 'notice'])
const MSG_KIND_SET = new Set<ChatMessageKind>(['text', 'image', 'file', 'card'])

function normalizeTime(value: number | string | undefined): string {
  if (value == null) return new Date().toISOString()
  if (typeof value === 'number') return new Date(value).toISOString()
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? new Date().toISOString() : date.toISOString()
}

function normalizeType(value?: string): MessageType | undefined {
  if (value && TYPE_SET.has(value as MessageType)) return value as MessageType
  return undefined
}

function normalizeConvKind(value?: string): ConversationKind {
  if (value === 'bot') return 'notice'
  if (value && KIND_SET.has(value as ConversationKind)) return value as ConversationKind
  return 'notice'
}

function normalizeMsgKind(value?: string): ChatMessageKind {
  if (value && MSG_KIND_SET.has(value as ChatMessageKind)) return value as ChatMessageKind
  return 'text'
}

function normalizeFrom(value?: string): ChatMessageFrom {
  if (value === 'self' || value === 'peer' || value === 'system') return value
  return 'peer'
}

export function mapConversationDto(dto: ImConversationDto): Conversation {
  const kind = normalizeConvKind(dto.kind)
  return {
    id: String(dto.id),
    kind,
    title: dto.title,
    subtitle: dto.subtitle,
    online: Boolean(dto.online),
    starred: Boolean(dto.starred),
    muted: Boolean(dto.muted),
    pinned: Boolean(dto.pinned) || dto.pinnedAt != null,
    pinnedAt: dto.pinnedAt != null ? normalizeTime(dto.pinnedAt) : undefined,
    labels: Array.isArray(dto.labels) ? [...dto.labels] : [],
    lastPreview: dto.lastPreview ?? '',
    lastTime: normalizeTime(dto.lastTime),
    unread: dto.unread ?? 0,
    type: normalizeType(dto.type),
    sender: dto.sender,
    bizTab: dto.bizTab,
    payload: dto.payload,
    members: dto.members?.map(
      (member): ConversationMember => ({
        id: String(member.id),
        name: member.name,
        kind: (member.kind as ConversationMember['kind']) ?? 'user',
        username: member.username,
        email: member.email,
      })
    ),
  }
}

export function mapMessageDto(dto: ImMessageDto): ChatMessage {
  return {
    id: String(dto.id),
    conversationId: String(dto.conversationId),
    from: normalizeFrom(dto.from),
    kind: normalizeMsgKind(dto.kind),
    content: dto.content ?? '',
    createTime: normalizeTime(dto.createTime),
    senderName: dto.senderName,
    fileName: dto.fileName,
    read: dto.read,
    payload: dto.payload,
  }
}

export function conversationToRecord(conversation: Conversation): MessageRecord {
  return {
    id: conversation.id,
    title: conversation.title,
    content: conversation.lastPreview,
    type: conversation.type ?? 'business',
    read: conversation.unread === 0,
    sender: conversation.sender ?? conversation.title,
    createTime: conversation.lastTime,
    bizTab: conversation.bizTab,
    payload: conversation.payload,
  }
}

export function recordsToInbox(records: MessageRecord[]): {
  conversations: Conversation[]
  threads: Record<string, ChatMessage[]>
} {
  const conversations = records.map((record) => ({
    id: record.id,
    kind: 'notice' as const,
    title: record.title,
    lastPreview: record.content,
    lastTime: record.createTime,
    unread: record.read ? 0 : 1,
    type: record.type,
    sender: record.sender,
    bizTab: record.bizTab,
    payload: record.payload,
  }))
  const threads: Record<string, ChatMessage[]> = {}
  for (const record of records) {
    threads[record.id] = [
      {
        id: `${record.id}-body`,
        conversationId: record.id,
        from: 'peer',
        kind: 'card',
        content: record.content,
        createTime: record.createTime,
        senderName: record.sender,
        payload: record.payload,
      },
    ]
  }
  return { conversations, threads }
}
